<script>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';

  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';

  const VARIANTS = {
    DEFAULT: {
      id: 'default',
    },
    OVER_IMAGE: {
      id: 'over-image',
      positions: ['top-right'],
    },
    DROPDOWN_ITEM: {
      id: 'dropdown-item',
    },
  };

  export default {
    name: 'ToggleStoreFavorite',

    components: {
      Button,
      Icon,
    },

    props: {
      /**
       * A product that can be added to the Store Favorites list.
       */
      product: {
        type: Object,
        required: true,
      },

      /**
       * Is the product currently on the Store Favorites list?
       */
      isFavorited: {
        type: Boolean,
        default: false,
      },

      isLoading: {
        type: Boolean,
        default: false,
      },

      /**
       * For the "source" property of the tracking event.
       * Ex: "Purchase History"
       */
      trackingSource: {
        type: String,
        required: true,
      },

      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return Object.values(VARIANTS)
            .map(({ id }) => id)
            .includes(value.toLowerCase());
        },
      },

      variantOptions: {
        type: Object,
        default: () => ({}),
        validator(value) {
          const isPositionValid = value?.position ? VARIANTS.OVER_IMAGE.positions.includes(value.position) : true;

          if (!isPositionValid) {
            throw new Error(
              `The variantOptions prop failed validation. The "position" property has an invalid value of ${JSON.stringify(
                value.position,
              )}.`,
            );
          }

          return true;
        },
      },
    },

    emits: ['before-toggle', 'after-toggle', 'success'],
    data() {
      return {
        isUpdatingFavoriteStatus: false,
      };
    },

    setup() {
      const { activeCompany } = storeToRefs(useCompanyStore());

      const shopStore = useShopStore();

      const { toggleFavoritesItem } = shopStore;

      const { getSplitTreatment } = storeToRefs(useUserStore());

      return {
        activeCompany,
        getSplitTreatment,
        toggleFavoritesItem,
      };
    },

    created() {
      this.VARIANTS = VARIANTS;
    },

    methods: {
      async toggleFavorite() {
        if (this.isLoading || this.isUpdatingFavoriteStatus || !this.activeCompany) {
          return;
        }

        this.$emit('before-toggle');

        this.isUpdatingFavoriteStatus = true;

        try {
          await this.toggleFavoritesItem({
            productId: this.product.id,
            trackingSource: this.trackingSource,
            userActiveCompany: this.activeCompany,
          });

          this.$emit('success');
        } finally {
          this.isUpdatingFavoriteStatus = false;
          this.$emit('after-toggle');
        }
      },
    },
  };
</script>

<template>
  <div
    v-if="activeCompany?.buyer"
    :class="{
      [$style['over-image']]: variant === VARIANTS.OVER_IMAGE.id,
      [$style[variantOptions.position]]: !!variantOptions.position,
    }"
  >
    <span v-if="isLoading || isUpdatingFavoriteStatus" class="block ll-pa-2">
      <Icon name="working" class="fx-spin" />
    </span>

    <button
      v-else-if="variant === VARIANTS.DROPDOWN_ITEM.id"
      :title="$t(`shop.${isFavorited ? 'removeFromStoreFavorites' : 'addToStoreFavorites'}`)"
      @click="toggleFavorite"
    >
      {{ isFavorited ? $t('shop.removeFromStoreFavorites') : $t('shop.addToStoreFavorites') }}
    </button>

    <Button
      v-else
      icon
      :class="$style['heart-button']"
      :title="$t(`shop.${isFavorited ? 'removeFromStoreFavorites' : 'addToStoreFavorites'}`)"
      v-bind="$attrs"
      @click="toggleFavorite"
    >
      <Icon v-if="isFavorited" name="heart-filled" class="color-red" :class="$style['heart-icon']" />
      <Icon v-else name="heart-outline" :class="$style['heart-icon']" />
    </Button>
  </div>
</template>

<style lang="scss" module>
  .over-image {
    background-color: rgba($ice-700, 0.2);
    border-radius: 100%;
    margin: space(2);
    position: absolute;

    &.top-right {
      right: 0;
      top: 0;
    }

    .heart-button {
      padding: space(1);
    }

    .heart-icon {
      height: space(4);
      width: space(4);
    }
  }
</style>
